import { useCallback } from 'react';

import { EMessageType } from '@/components/EditableMessage/types';
import { useAvailablePaths } from '@/components/EvaluatorCrudPane/hooks/useAvailablePaths';
import { useVariableMappingProps } from '@/components/EvaluatorCrudPane/utils/useVariableMappingProps';
import { MessageCapability } from '@/types/schema';

import {
  useDeep,
  usePlaygroundStore,
} from '../Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { Manifest } from '../Playground/components/manifest/Manifest';
import { manifestModelAndProvider } from '../Playground/components/manifest/serialized/ManifestConstructor';
import { getOpenAIModelCapabilities } from '../Playground/components/manifest/serialized/chat_models/ManifestChatOpenAI.utils';
import { EvaluatorPlaygroundPromptTopBar } from './EvaluatorPlaygroundPromptTopBar';

export function EvaluatorPromptPanel({
  promptCollapseState,
  setPromptCollapseState,
}: {
  promptCollapseState: boolean;
  setPromptCollapseState: (index: number, isAllCollapsed?: boolean) => void;
}) {
  const variableMappingProps = useVariableMappingProps();

  const examples = usePlaygroundStore((state) => state.examples);

  useAvailablePaths(
    examples?.[0]?.inputs?.input,
    examples?.[0]?.inputs?.output,
    examples?.[0]?.inputs?.referenceOutput,
    !examples?.[0]?.inputs
  );
  const manifest = usePlaygroundStore(
    useDeep((state) => state.prompts[0].manifest)
  );

  const options = usePlaygroundStore(
    useDeep((state) => state.prompts[0].options)
  );
  const hubPrompt = usePlaygroundStore(
    useDeep((state) => state.prompts[0].hubPrompt)
  );

  const setManifest = usePlaygroundStore((state) => state.setManifest);
  const setPrompt = usePlaygroundStore((state) => state.setPrompt);

  const manifestLast = manifest.kwargs.last;

  const modelAndProvider = manifestModelAndProvider({
    variant: 'line',
    value: manifestLast,
    options: {},
  });

  const onChangeManifest = useCallback(
    (value: Record<string, unknown>) => {
      setManifest(0, (prev) => ({
        ...prev,
        kwargs: { ...prev.kwargs, first: value },
      }));
      setPrompt(0, (prev) => ({
        ...prev,
        hubPrompt: prev.hubPrompt
          ? {
              ...prev.hubPrompt,
              edited: true,
            }
          : undefined,
      }));
    },
    [setManifest, setPrompt]
  );

  const { isSystemMessagesSupported } = getOpenAIModelCapabilities(
    modelAndProvider?.model ?? ''
  );

  return (
    <div className="my-3 flex h-full flex-col gap-1 overflow-y-hidden px-2">
      {hubPrompt?.commitHash?.slice(0, 8) !==
        hubPrompt?.prompt.last_commit_hash?.slice(0, 8) && (
        <div className="mb-2 rounded-md border border-warning bg-warning-primary p-1 text-xs">
          You're viewing an older version of this prompt. Committing changes
          will overwrite the latest version.
        </div>
      )}
      <EvaluatorPlaygroundPromptTopBar
        isAllCollapsed={promptCollapseState}
        setIsAllCollapsed={() => setPromptCollapseState(0)}
      />

      <div className={'mt-0 flex h-full flex-col gap-2 overflow-y-hidden pr-4'}>
        <div className="-ml-1 h-full overflow-x-hidden">
          <Manifest
            variant="evaluator"
            value={manifest.kwargs.first}
            onChange={onChangeManifest}
            options={options}
            onOptionsChange={() => void 0}
            hideTemplateFormatSelector
            outputSchemaAsModal
            minimizedSchema={true}
            disabledMessageTypes={
              !isSystemMessagesSupported ? [EMessageType.SYSTEM] : []
            }
            disabledMessageCapabilities={[
              MessageCapability.CANVAS,
              MessageCapability.IMAGE,
            ]}
            isAllCollapsed={promptCollapseState}
            setIsAllCollapsed={(isAllCollapsed) =>
              setPromptCollapseState(0, isAllCollapsed)
            }
            variableMappingProps={variableMappingProps}
          />
        </div>
      </div>
    </div>
  );
}
